import React, { useState, useEffect } from 'react';
import DomainForm from '../../components/risk/DomainForm';
import SectionForm from '../../components/risk/SectionForm';
import QuestionForm from '../../components/risk/QuestionForm';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function SurveyBuilder() {
  const [domains, setDomains] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(''); // '' = Default
  const [newSurveyName, setNewSurveyName] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('domains');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const sessionUser = (() => { try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; } })();
  const riskRole = sessionUser?.app_roles?.risk?.role;
  const canDelete = riskRole === 'admin' || riskRole === 'officer';

  // Fetch surveys on mount; fetch domains when template selection changes (keeps list in sync)
  useEffect(() => {
    fetchSurveys();
  }, []);
  useEffect(() => {
    fetchDomains();
  }, [selectedSurveyId]);

  // Fetch sections when domain is selected
  useEffect(() => {
    if (selectedDomain) {
      fetchSections(selectedDomain.id);
    } else {
      setSections([]);
      setSelectedSection(null);
      setQuestions([]);
    }
  }, [selectedDomain]);

  // Fetch questions when section is selected
  useEffect(() => {
    if (selectedSection) {
      fetchQuestions(selectedSection.id);
    } else {
      setQuestions([]);
    }
  }, [selectedSection]);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const query = selectedSurveyId ? `?survey_id=${selectedSurveyId}` : '';
      const res = await fetch(`${API_BASE}/risk-assessment/domains${query}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setDomains(data.domains);
      } else {
        setMessage('❌ Failed to load domains');
      }
    } catch (err) {
      setMessage('❌ Error loading domains');
      console.error(err);
    }
    setLoading(false);
  };

  const fetchSurveys = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/surveys`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setSurveys(data.surveys || []);
    } catch (err) {
      console.error('Error loading surveys:', err);
    }
  };

  const fetchSections = async (domainId) => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/domains/${domainId}/sections`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setSections(data.sections);
      }
    } catch (err) {
      console.error('Error loading sections:', err);
    }
  };

  const fetchQuestions = async (sectionId) => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/sections/${sectionId}/questions`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions);
      }
    } catch (err) {
      console.error('Error loading questions:', err);
    }
  };

  const handleDomainCreated = (domain) => {
    setDomains([...domains, domain]);
    setMessage('✅ Domain created successfully');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleSectionCreated = (section) => {
    setSections([...sections, section]);
    setMessage('✅ Section created successfully');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleQuestionCreated = (question) => {
    setQuestions([...questions, question]);
    setMessage('✅ Question created successfully');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDomainSelect = (domain) => {
    setSelectedDomain(domain);
    setSelectedSection(null);
    setActiveTab('sections');
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setActiveTab('questions');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
        <h1 className="text-4xl font-bold mb-3">Risk Assessment Survey Builder</h1>
        <p className="text-slate-300 text-lg">
          Create and manage survey questions for annual risk assessments
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl text-sm font-medium ${
          message.startsWith('✅') 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {[
            { id: 'domains', label: 'Risk Domains', count: domains.length },
            { id: 'sections', label: 'Sections', count: sections.length, disabled: !selectedDomain },
            { id: 'questions', label: 'Questions', count: questions.length, disabled: !selectedSection }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : tab.disabled
                  ? 'border-transparent text-slate-400 cursor-not-allowed'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              disabled={tab.disabled}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {activeTab === 'domains' && (
            <div className="space-y-6">
              <p className="text-sm text-slate-600 font-medium">
                Building for template: <span className="text-slate-900">{selectedSurveyId ? (surveys.find(s => s.id === selectedSurveyId)?.name || 'Unknown') : 'Default'}</span>
                {' — '}new domains will be added to this template.
              </p>
              <DomainForm onDomainCreated={handleDomainCreated} surveyId={selectedSurveyId || null} />
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Existing Domains</h3>
                <div className="space-y-3">
                  {domains.map((domain) => (
                    <div key={domain.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => handleDomainSelect(domain)}>
                          <h4 className="font-medium text-slate-900">{domain.name}</h4>
                          {domain.description && (
                            <p className="text-sm text-slate-600 mt-1">{domain.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-xs text-slate-500">Order: {domain.display_order}</div>
                          {canDelete && (
                            <button
                              onClick={async () => {
                                if (!window.confirm('Delete this domain and all its sections and questions?')) return;
                                try {
                                  const res = await fetch(`${API_BASE}/risk-assessment/domains/${domain.id}`, { method: 'DELETE', credentials: 'include' });
                                  const json = await res.json().catch(() => ({}));
                                  if (res.ok && (json.success !== false)) {
                                    setDomains(prev => prev.filter(d => d.id !== domain.id));
                                    if (selectedDomain?.id === domain.id) { setSelectedDomain(null); setSections([]); setSelectedSection(null); setQuestions([]); }
                                  } else { alert(json.message || 'Failed to delete domain'); }
                                } catch (_) { alert('Network error'); }
                              }}
                              className="text-red-600 text-sm hover:text-red-800"
                              title="Delete domain"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sections' && selectedDomain && (
            <div className="space-y-6">
              <SectionForm 
                domainId={selectedDomain.id} 
                onSectionCreated={handleSectionCreated} 
              />
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Sections in {selectedDomain.name}
                </h3>
                <div className="space-y-3">
                  {sections.map((section) => (
                    <div key={section.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => handleSectionSelect(section)}>
                          <h4 className="font-medium text-slate-900">{section.name}</h4>
                          {section.description && (
                            <p className="text-sm text-slate-600 mt-1">{section.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-xs text-slate-500">Order: {section.display_order}</div>
                          {canDelete && (
                            <button
                              onClick={async () => {
                                if (!window.confirm('Delete this section and all its questions?')) return;
                                try {
                                  const res = await fetch(`${API_BASE}/risk-assessment/sections/${section.id}`, { method: 'DELETE', credentials: 'include' });
                                  const json = await res.json().catch(() => ({}));
                                  if (res.ok && (json.success !== false)) {
                                    setSections(prev => prev.filter(s => s.id !== section.id));
                                    if (selectedSection?.id === section.id) { setSelectedSection(null); setQuestions([]); }
                                  } else { alert(json.message || 'Failed to delete section'); }
                                } catch (_) { alert('Network error'); }
                              }}
                              className="text-red-600 text-sm hover:text-red-800"
                              title="Delete section"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'questions' && selectedSection && (
            <div className="space-y-6">
              <QuestionForm 
                sectionId={selectedSection.id} 
                onQuestionCreated={handleQuestionCreated} 
              />
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Questions in {selectedSection.name}
                </h3>
                <div className="space-y-4">
                  {questions.map((question) => (
                    <div key={question.id} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 mb-2">{question.question_text}</p>
                          <div className="flex flex-wrap gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              question.question_type === 'control_rating' 
                                ? 'bg-amber-100 text-amber-800'
                                : question.question_type === 'scale'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {question.question_type.replace('_', ' ')}
                            </span>
                            {question.required && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">Required</span>
                            )}
                            {question.control_required && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">Control Rating Required</span>
                            )}
                            <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">Weight: {question.scoring_weight}</span>
                          </div>
                        </div>
                        {canDelete && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('Delete this question?')) return;
                              try {
                                const res = await fetch(`${API_BASE}/risk-assessment/questions/${question.id}`, { method: 'DELETE', credentials: 'include' });
                                const json = await res.json().catch(() => ({}));
                                if (res.ok && (json.success !== false)) {
                                  setQuestions(prev => prev.filter(q => q.id !== question.id));
                                } else { alert(json.message || 'Failed to delete question'); }
                              } catch (_) { alert('Network error'); }
                            }}
                            className="text-red-600 text-sm hover:text-red-800"
                            title="Delete question"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Survey templates */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Survey Template</h3>
            <div className="space-y-3">
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                value={selectedSurveyId}
                onChange={(e) => { setSelectedSurveyId(e.target.value); setSelectedDomain(null); setSelectedSection(null); setQuestions([]); setDomains([]); }}
              >
                <option value="">Default</option>
                {surveys.map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (Default)' : ''}</option>
                ))}
              </select>

              <div className="pt-3 border-t border-slate-200">
                <label className="block text-sm font-medium text-slate-700 mb-2">Create New Template</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSurveyName}
                    onChange={(e) => setNewSurveyName(e.target.value)}
                    placeholder="e.g., Survey A"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={async () => {
                      const name = newSurveyName.trim();
                      if (!name) return;
                      try {
                        const res = await fetch(`${API_BASE}/risk-assessment/surveys`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ name })
                        });
                        const data = await res.json();
                        if (data.success) {
                          setSurveys(prev => [...prev, data.survey]);
                          setSelectedSurveyId(data.survey.id);
                          setNewSurveyName('');
                          fetchDomains();
                          setMessage('✅ Survey template created');
                          setTimeout(() => setMessage(''), 3000);
                        }
                      } catch (err) {}
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Breadcrumb */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Current Selection</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="text-slate-500">Template:</span>
                <span className="ml-2 font-medium text-slate-900">
                  {selectedSurveyId ? (surveys.find(s => s.id === selectedSurveyId)?.name || 'Unknown') : 'Default'}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-slate-500">Domain:</span>
                <span className="ml-2 font-medium text-slate-900">
                  {selectedDomain ? selectedDomain.name : 'None selected'}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-slate-500">Section:</span>
                <span className="ml-2 font-medium text-slate-900">
                  {selectedSection ? selectedSection.name : 'None selected'}
                </span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Survey Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Total Domains</span>
                <span className="text-sm font-medium text-slate-900">{domains.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Total Sections</span>
                <span className="text-sm font-medium text-slate-900">
                  {domains.reduce((sum, domain) => sum + (domain.section_count || 0), 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600">Total Questions</span>
                <span className="text-sm font-medium text-slate-900">
                  {domains.reduce((sum, domain) => sum + (domain.question_count || 0), 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Help */}
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
            <h3 className="text-sm font-medium text-blue-900 mb-3">💡 Survey Builder Guide</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p><strong>1. Pick a template:</strong> Choose the survey template (e.g. department) above. All domains/sections/questions you add will belong to that template.</p>
              <p><strong>2. Create domains:</strong> Add risk areas (e.g. Prior Authorization, Compliance) under the selected template.</p>
              <p><strong>3. Add sections:</strong> Group related questions within each domain.</p>
              <p><strong>4. Build questions:</strong> Add assessment questions with scoring. In Scope &amp; Assign, assign this template to the matching department.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
